import { ImageSlot } from './ImageSlot';
import type { CardSize, Orientation, SlotId, SlotImage, SlotMap } from '../types';

interface Props {
  slots: SlotMap;
  cardSize: CardSize;
  orientation: Orientation;
  preserveResolution: boolean;
  onSlotChange: (slotId: SlotId, next: SlotImage | undefined) => void;
  onOrientationChange?: (next: Orientation) => void;
  insideEnabled?: boolean;
}

interface SlotMeta {
  id: SlotId;
  labelPortrait: string;
  labelLandscape: string;
  required?: boolean;
}

const OUTSIDE_SLOTS: SlotMeta[] = [
  { id: 'front', labelPortrait: 'Front', labelLandscape: 'Front', required: true },
  { id: 'back', labelPortrait: 'Back', labelLandscape: 'Back' },
];

const INSIDE_SLOTS: SlotMeta[] = [
  { id: 'insideLeft', labelPortrait: 'Inside-left', labelLandscape: 'Inside-top' },
  { id: 'insideRight', labelPortrait: 'Inside-right', labelLandscape: 'Inside-bottom' },
];

const labelFor = (meta: SlotMeta, orientation: Orientation) =>
  orientation === 'landscape' ? meta.labelLandscape : meta.labelPortrait;

/** Image-vs-card-orientation mismatch detection. Image with aspect > 1.15 on
 *  a portrait card (or aspect < 0.87 on a landscape card) is "wrong way." */
const detectMismatch = (
  slots: SlotMap,
  orientation: Orientation
): { axis: 'landscape' | 'portrait'; sample: SlotImage } | null => {
  for (const id of ['front', 'back', 'insideLeft', 'insideRight'] as SlotId[]) {
    const slot = slots[id];
    if (!slot) continue;
    const ratio = slot.width / slot.height;
    if (orientation === 'portrait' && ratio > 1.15) return { axis: 'landscape', sample: slot };
    if (orientation === 'landscape' && ratio < 0.87) return { axis: 'portrait', sample: slot };
  }
  return null;
};

export function ImageInputs({
  slots,
  cardSize,
  orientation,
  preserveResolution,
  onSlotChange,
  onOrientationChange,
  insideEnabled = true,
}: Props) {
  const slotsToRender = insideEnabled ? [...OUTSIDE_SLOTS, ...INSIDE_SLOTS] : OUTSIDE_SLOTS;
  const mismatch = onOrientationChange ? detectMismatch(slots, orientation) : null;

  return (
    <section className="card-section image-inputs">
      <h2>2. Drop in your images</h2>

      {mismatch && onOrientationChange && (
        <div className="orient-mismatch" role="status">
          <span className="orient-mismatch-text">
            Your image is <strong>{mismatch.axis}</strong>
            {' '}({mismatch.sample.width}×{mismatch.sample.height}) but the card is{' '}
            <strong>{orientation}</strong>. Switch the card to {mismatch.axis}?
          </span>
          <button
            type="button"
            className="orient-mismatch-btn"
            onClick={() => onOrientationChange(mismatch.axis)}
          >
            Switch to {mismatch.axis}
          </button>
        </div>
      )}

      <div className={`slot-grid slot-grid-${slotsToRender.length}`}>
        {slotsToRender.map((meta) => (
          <ImageSlot
            key={meta.id}
            slotId={meta.id}
            label={labelFor(meta, orientation)}
            required={meta.required}
            image={slots[meta.id]}
            cardSize={cardSize}
            orientation={orientation}
            preserveResolution={preserveResolution}
            onChange={onSlotChange}
          />
        ))}
      </div>
    </section>
  );
}
